import { useRef } from 'react'
import * as React from 'react'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import styled from 'styled-components'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import type { OfferPageMultiRoomAutoselectionReason, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import type {
  LoadingStatusByRoom,
  PackageOptionViewByRoom,
  PackagesOptionsViewsByRoom,
} from 'selectors/offerPage/multipleRoomBookingSelectors'
import {
  getActivePackagesByRoom,
  getAvailablePackagesByRoom,
  getCalendarsToBeFetchedByRoom, getIsLoadingByRoom,
} from 'selectors/offerPage/multipleRoomBookingSelectors'
import { EmptyArray } from 'lib/array/arrayUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import MultiIndividualRoomRoom from '../MultipleRoomsBooking/MultiIndividualRoomRoom'
import PreSelectionBanner from '../MultipleRoomsBooking/PreSelectionBanner'
import InvalidSelectionBanner from '../MultipleRoomsBooking/InvalidSelectionBanner'
import MultipleRoomsIndividualRoomsFooter from '../MultipleRoomsBooking/MultipleRoomsIndividualRoomsFooter'

import GrandHyattBaliFutureDatesBanner, {
  shouldShowGrandHyattBaliFutureDatesBanner,
} from './GrandHyattBaliFutureDatesBanner'

const PackageOptionsContainer = styled(LayoutContainer)`
  position: relative;

  ${mediaQueryDown.mobile} {
    padding: 0;
  }
`

const GreyGroup = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

  ${mediaQueryUp.tablet} {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }
`

interface Props {
  rooms?: Array<OfferPageRoom>
  offer: App.HotelOffer
  activePackagesByRoom: PackageOptionViewByRoom
  isLoadingByRoom: LoadingStatusByRoom
  calendarsToBeFetchedByRoom: LoadingStatusByRoom
  availablePackagesByRoom: PackagesOptionsViewsByRoom
  account?: App.AuthAccount
  autoselectionReason?: OfferPageMultiRoomAutoselectionReason
}

function FlashHotelDesktopIndividualRooms({
  rooms = EmptyArray,
  offer,
  activePackagesByRoom,
  isLoadingByRoom,
  calendarsToBeFetchedByRoom,
  availablePackagesByRoom,
  autoselectionReason,
}: Props) {
  const packageStartRef = useRef<HTMLDivElement>(null)

  const roomsAreSelected = rooms.some(room => room.roomId)
  let selectionBanner: React.ReactNode = null
  if (roomsAreSelected) {
    if (autoselectionReason === 'initial-selection') {
      selectionBanner = <PreSelectionBanner saleUnit={offer.saleUnit} />
    } else if (autoselectionReason === 'invalid-selections') {
      selectionBanner = <InvalidSelectionBanner saleUnit={offer.saleUnit} />
    }
  }

  const showGrandHyattBaliFutureDatesBanner = shouldShowGrandHyattBaliFutureDatesBanner(offer.id)

  return (
    <VerticalSpacer gap={16}>
      {showGrandHyattBaliFutureDatesBanner && <LayoutContainer>
        <GrandHyattBaliFutureDatesBanner />
      </LayoutContainer>}
      <Pane ref={packageStartRef}>
        <PackageOptionsContainer>
          <CSSBreakpoint max="mobile">
            <Divider kind="primary" />
          </CSSBreakpoint>
          <GreyGroup direction="vertical" gap={8} tabletGap={24}>
            {rooms.map((room, i) => {
              const packageOptionView = activePackagesByRoom[room.id]
              const roomTile = <MultiIndividualRoomRoom
                key={room.id}
                room={room}
                packageNumber={i + 1}
                packageView={packageOptionView}
                isFetching={!!(isLoadingByRoom[room.id] || calendarsToBeFetchedByRoom[room.id])}
                availablePackages={availablePackagesByRoom[room.id]}
              />

              if (i === 0 && selectionBanner) {
                return <div key={room.id}>
                  {selectionBanner}
                  {roomTile}
                </div>
              } else {
                return roomTile
              }
            })}

            <MultipleRoomsIndividualRoomsFooter />
          </GreyGroup>
        </PackageOptionsContainer>
      </Pane>
    </VerticalSpacer>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  offer: state.offer,
  account: appState.auth.account,
  activePackagesByRoom: getActivePackagesByRoom(state, appState),
  isLoadingByRoom: getIsLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
  autoselectionReason: state.multiRoomAutoselectionReason,
}), FlashHotelDesktopIndividualRooms)
